import { type CSSProperties, type RefObject, type SyntheticEvent, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface SearchSuggestionsDropdownProps<T> {
  canGoNext: boolean;
  canGoPrevious: boolean;
  dropdownRef: RefObject<HTMLDivElement | null>;
  dropdownStyle: CSSProperties;
  getColumnDisplayValue: (row: T, column: { field: string; width?: number; flex?: number }) => string;
  getColumnStyle: (column: { field: string; width?: number; flex?: number }, index: number) => CSSProperties;
  getItemKey: (row: T, index: number) => string;
  highlightedIndex: number;
  isLoading: boolean;
  onCommitSelection: (row: T) => void;
  onHighlightIndexChange: (index: number) => void;
  onNextPage: () => void;
  onPreviousPage: () => void;
  showNoResults: boolean;
  showPagination: boolean;
  shouldShow: boolean;
  suggestionColumns: Array<{ field: string; width?: number; flex?: number }>;
  suggestions: T[];
}

export const SearchSuggestionsDropdown = <T,>({
  canGoNext,
  canGoPrevious,
  dropdownRef,
  dropdownStyle,
  getColumnDisplayValue,
  getColumnStyle,
  getItemKey,
  highlightedIndex,
  isLoading,
  onCommitSelection,
  onHighlightIndexChange,
  onNextPage,
  onPreviousPage,
  showNoResults,
  showPagination,
  shouldShow,
  suggestionColumns,
  suggestions
}: SearchSuggestionsDropdownProps<T>) => {
  const selectionPointerHandledRef = useRef(false);

  if (!shouldShow || typeof document === 'undefined') {
    return null;
  }

  const stopDropdownEvent = (event: SyntheticEvent) => {
    event.stopPropagation();
  };

  const preventDropdownMouseDown = (event: SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const getSuggestionListMaxHeight = () => {
    const maxHeight = dropdownStyle.maxHeight;

    if (typeof maxHeight !== 'number') {
      return maxHeight;
    }

    return Math.max(96, maxHeight - (showPagination ? 45 : 0));
  };

  return createPortal(
    <div
      ref={dropdownRef}
      data-search-suggestions-dropdown='true'
      className='pointer-events-auto fixed z-[9999] flex min-w-80 flex-col overflow-hidden rounded-md border border-border bg-popover text-popover-foreground shadow-md'
      style={dropdownStyle}
    >
      {isLoading && <div className='px-3 py-2 text-sm text-muted-foreground'>Đang tìm kiếm...</div>}

      {!isLoading && (
        <div
          className='min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain'
          style={{ maxHeight: getSuggestionListMaxHeight() }}
          onWheel={stopDropdownEvent}
        >
          {suggestions.map((row, index) => {
            const isActive = index === highlightedIndex;
            const itemKey = getItemKey(row, index);
            const handleSelect = (event: SyntheticEvent) => {
              event.preventDefault();
              event.stopPropagation();

              if (selectionPointerHandledRef.current) {
                return;
              }

              selectionPointerHandledRef.current = true;
              onCommitSelection(row);
              window.setTimeout(() => {
                selectionPointerHandledRef.current = false;
              }, 0);
            };

            return (
              <button
                type='button'
                key={itemKey}
                className={cn(
                  'flex min-h-[var(--erp-control-height)] w-full border-b border-border text-left text-[11px] transition-colors last:border-b-0',
                  isActive ? 'bg-amber-100 text-foreground' : 'hover:bg-accent hover:text-accent-foreground'
                )}
                onMouseEnter={() => onHighlightIndexChange(index)}
                onPointerDown={handleSelect}
                onMouseDown={handleSelect}
              >
                {suggestionColumns.map((column, columnIndex) => {
                  const columnValue = getColumnDisplayValue(row, column);

                  return (
                    <span
                      key={`${column.field || columnIndex}`}
                      className={cn(
                        'flex min-h-[var(--erp-control-height)] min-w-0 items-center break-words px-3 py-1 leading-snug',
                        columnIndex === 0 ? 'font-medium text-foreground' : 'text-muted-foreground'
                      )}
                      style={getColumnStyle(column, columnIndex)}
                      title={columnValue}
                    >
                      {columnValue}
                    </span>
                  );
                })}
              </button>
            );
          })}
        </div>
      )}

      {showPagination && (
        <div className='flex items-center justify-end gap-2 border-t border-border bg-popover px-2 py-2'>
          <Button
            type='button'
            variant='outline'
            size='icon'
            className='size-7 rounded-sm'
            disabled={!canGoPrevious || isLoading}
            onPointerDown={preventDropdownMouseDown}
            onMouseDown={preventDropdownMouseDown}
            onClick={onPreviousPage}
          >
            &lt;
          </Button>
          <Button
            type='button'
            variant='outline'
            size='icon'
            className='size-7 rounded-sm'
            disabled={!canGoNext || isLoading}
            onPointerDown={preventDropdownMouseDown}
            onMouseDown={preventDropdownMouseDown}
            onClick={onNextPage}
          >
            &gt;
          </Button>
        </div>
      )}

      {showNoResults && <div className='px-3 py-2 text-sm text-muted-foreground'>Không tìm thấy kết quả</div>}
    </div>,
    document.body
  );
};
